import { Color, PaneAmount, PaneItemChild, PaymentPaneProps, Text } from '@billy/design-system'
import { TFunction } from 'i18next'
import React from 'react'

import { ConnectBankBanner } from '@features/bankAccounts/elements/ConnectBankBanner'
import type { ConnectBankBannerState } from '@features/bankAccounts/hooks/useConnectBankBanner'
import { BillStatus } from '@features/bills/enums/billStatus'
import { Bill } from '@features/bills/types/bill'
import { getBillStatus } from '@features/bills/utils/getBillStatus'
import { UiSource } from '@features/reconciliation/enums'

import { billStatusToPaymentPaneStatus } from '../constants/billStatusToPaymentPaneStatus'
import { BillPaymentPaneExtra } from '../elements/BillPaymentPaneExtra'
import { BillPaymentPanePrefix } from '../elements/BillPaymentPanePrefix'

export const getBillPaymentPaneData = (
  t: TFunction,
  billData?: Bill,
  isLoading?: boolean,
  connectBankBanner?: ConnectBankBannerState,
): PaymentPaneProps => {
  if (!billData || isLoading) {
    return { isLoading }
  }

  const { amount, balance, balanceModifiers, dueDate, entryDate, isPaid, state, tax } = billData

  const billStatus = getBillStatus({
    amount,
    balance,
    dueDate: dueDate ? new Date(dueDate) : undefined,
    isPaid,
    normalizePartiallyPaid: true,
    state,
    tax,
  })

  const modifiersCount = balanceModifiers?.length
  const isPaidOrDraftBillStatus = billStatus === BillStatus.Paid || billStatus === BillStatus.Draft
  const paidDate = modifiersCount ? balanceModifiers[0].entryDate : entryDate
  const isUnpaidOrOverdue = billStatus === BillStatus.Overdue || billStatus === BillStatus.Unpaid
  const amountColor = isUnpaidOrOverdue ? Color.Red700 : Color.Grey200

  return {
    amount: (
      <PaneItemChild>
        <PaneAmount amount={balance} color={amountColor} />
      </PaneItemChild>
    ),
    banner: isUnpaidOrOverdue && connectBankBanner?.isVisible && (
      <ConnectBankBanner
        uiSource={UiSource.PurchaseScreen}
        variant="purchase"
        onDismiss={connectBankBanner.onDismiss}
      />
    ),
    collapsible: !!modifiersCount,
    content: !!modifiersCount && (
      <Text variant="bodySM">{t('bills.view.payment_pane.content.payment', { count: modifiersCount })}</Text>
    ),
    extra: (
      <PaneItemChild>
        <BillPaymentPaneExtra date={isPaidOrDraftBillStatus ? paidDate : dueDate} status={billStatus} />
      </PaneItemChild>
    ),
    icon: 'circleAlert',
    prefix: <BillPaymentPanePrefix status={billStatus} />,
    status: billStatusToPaymentPaneStatus[billStatus],
  }
}
